import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { CalendarClock, Plus, Pencil, Trash2, AlertTriangle, Brain } from "lucide-react";

const ELIG = ["Eligible", "Semi-Eligible", "Not Eligible"];
const empty = { bid_number: "", item_name: "", quantity: "", last_date: "", eligibility: "Eligible", notes: "" };

export function rowColor(days) {
  if (days === null || days === undefined) return "bg-white";
  if (days <= 2) return "bg-red-50 border-l-4 border-red-500";
  if (days <= 7) return "bg-amber-50 border-l-4 border-amber-400";
  return "bg-emerald-50/40 border-l-4 border-emerald-400";
}
const eligBadge = (e) =>
  e === "Eligible" ? "bg-emerald-100 text-emerald-700"
    : e === "Semi-Eligible" ? "bg-amber-100 text-amber-700"
      : "bg-red-100 text-red-700";

export default function BidTracker() {
  const navigate = useNavigate();
  const [bids, setBids] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/tracker/bids").then((r) => setBids(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setEdit(null); setForm(empty); setOpen(true); };
  const openEdit = (b) => {
    setEdit(b);
    setForm({ bid_number: b.bid_number, item_name: b.item_name || "", quantity: b.quantity || "",
      last_date: b.last_date || "", eligibility: b.eligibility || "Eligible", notes: b.notes || "" });
    setOpen(true);
  };
  const submit = async () => {
    if (!form.bid_number.trim()) return toast.error("Bid number is required");
    if (!form.last_date) return toast.error("Last date to apply is required");
    try {
      if (edit) await api.put(`/tracker/bids/${edit.id}`, form);
      else await api.post("/tracker/bids", form);
      toast.success(edit ? "Bid updated" : "Bid added");
      setOpen(false); load();
    } catch { toast.error("Could not save bid"); }
  };
  const remove = async (id) => { await api.delete(`/tracker/bids/${id}`); toast.success("Bid removed"); load(); };

  const soon = bids.filter((b) => b.days_left !== null && b.days_left <= 2);

  return (
    <div className="px-6 lg:px-12 py-8 lg:py-12 max-w-6xl">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-emerald-700">Bid Tracker</div>
          <h1 className="font-head text-2xl sm:text-3xl font-extrabold tracking-tight mt-2 flex items-center gap-2">
            <CalendarClock className="h-6 w-6 text-slate-900" /> Bid Management
          </h1>
          <p className="text-sm text-slate-500 mt-1">Track deadlines & eligibility. Reminders email you 2 days before a bid closes.</p>
        </div>
        <Button onClick={openNew} className="rounded-full bg-slate-900 hover:bg-slate-800" data-testid="add-tracked-bid-btn">
          <Plus className="h-4 w-4 mr-2" /> Add Bid
        </Button>
      </div>

      {soon.length > 0 && (
        <div className="mt-6 rounded-xl bg-red-50 border border-red-200 p-4 flex items-start gap-3" data-testid="closing-soon-banner">
          <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5 shrink-0" />
          <div className="text-sm text-red-800">
            <strong>{soon.length} bid(s) closing within 2 days:</strong>{" "}
            {soon.map((b) => `${b.bid_number} (${b.last_date})`).join(", ")}
          </div>
        </div>
      )}

      <Card className="rounded-xl border-border overflow-hidden mt-6">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="bid-tracker-table">
            <thead>
              <tr className="bg-slate-900 text-white text-left">
                <th className="px-4 py-3 font-semibold">Bid Number</th>
                <th className="px-4 py-3 font-semibold">Item</th>
                <th className="px-4 py-3 font-semibold">Qty</th>
                <th className="px-4 py-3 font-semibold">Last Date</th>
                <th className="px-4 py-3 font-semibold">Days Left</th>
                <th className="px-4 py-3 font-semibold">Eligibility</th>
                <th className="px-4 py-3 w-20"></th>
              </tr>
            </thead>
            <tbody>
              {bids.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400" data-testid="tracker-empty">No bids tracked yet. Add your first bid above.</td></tr>
              )}
              {bids.map((b) => (
                <tr key={b.id} className={`border-t border-slate-100 ${rowColor(b.days_left)}`} data-testid={`tracker-row-${b.id}`}>
                  <td className="px-4 py-3 font-medium text-slate-900">{b.bid_number}</td>
                  <td className="px-4 py-3 text-slate-600">{b.item_name || "—"}</td>
                  <td className="px-4 py-3 text-slate-600">{b.quantity || "—"}</td>
                  <td className="px-4 py-3 text-slate-600">{b.last_date || "—"}</td>
                  <td className="px-4 py-3 font-semibold text-slate-900">{b.days_left === null ? "—" : b.days_left < 0 ? "Closed" : `${b.days_left} day(s)`}</td>
                  <td className="px-4 py-3"><span className={`text-xs px-2 py-1 rounded-full font-medium ${eligBadge(b.eligibility)}`}>{b.eligibility}</span></td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1">
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-indigo-600" title="Bid Intelligence" onClick={() => navigate(`/tracker/${b.id}/intel`)} data-testid={`intel-tracker-${b.id}`}><Brain className="h-3.5 w-3.5" /></Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openEdit(b)} data-testid={`edit-tracker-${b.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600" onClick={() => remove(b.id)} data-testid={`delete-tracker-${b.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{edit ? "Edit Bid" : "Add Bid"}</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <Label className="text-xs font-semibold text-slate-600">Bid Number *</Label>
              <Input data-testid="tb-bid_number" value={form.bid_number} onChange={(e) => setForm({ ...form, bid_number: e.target.value })} placeholder="GEM/2026/B/1234567" className="mt-1.5" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-600">Product / Item</Label>
              <Input data-testid="tb-item_name" value={form.item_name} onChange={(e) => setForm({ ...form, item_name: e.target.value })} className="mt-1.5" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-600">Quantity</Label>
              <Input data-testid="tb-quantity" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} className="mt-1.5" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-600">Last Date to Apply *</Label>
              <Input data-testid="tb-last_date" type="date" value={form.last_date} onChange={(e) => setForm({ ...form, last_date: e.target.value })} className="mt-1.5" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-600">Eligibility</Label>
              <select data-testid="tb-eligibility" value={form.eligibility} onChange={(e) => setForm({ ...form, eligibility: e.target.value })} className="mt-1.5 w-full rounded-md border border-slate-200 px-3 py-2 text-sm">
                {ELIG.map((e) => <option key={e} value={e}>{e}</option>)}
              </select>
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs font-semibold text-slate-600">Notes</Label>
              <Textarea data-testid="tb-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} className="mt-1.5 resize-none" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-lg">Cancel</Button>
            <Button onClick={submit} className="rounded-lg bg-slate-900 hover:bg-slate-800" data-testid="save-tracker-btn">Save Bid</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
